// City level: a stylised street plan around each mission city, generated
// deterministically per city and clipped to the coastline, so roads never run into
// the sea. Districts are generic, fictional labels — not administrative areas.

import { s } from './mapRenderer.js';
import { project } from './mapData.js';

function rng(seed) {
  let h = 2166136261;
  for (const ch of seed) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return () => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return ((h >>> 0) % 100000) / 100000; };
}
const pts = (arr) => arr.map((p) => p.map((v) => v.toFixed(3)).join(',')).join(' ');

/** Street plan + districts + parks for one city, in map units. */
export function cityDetail(city) {
  const r = rng(city.id);
  const [cx, cy] = project(...city.at);
  const [sx, sy] = project(...city.site.at);
  const theta = r() * Math.PI;
  const g = s('g', { class: 'city-detail', 'data-city': city.domainId, 'clip-path': 'url(#ph-land-clip)' });

  // districts (soft colour zones)
  const tints = ['purple', 'cyan', 'pink', 'yellow'];
  city.districts.forEach(([, dx, dy], i) => g.append(s('circle', { class: `district-zone t-${tints[i % 4]}`, cx: cx + dx, cy: cy + dy, r: 1.25 + r() * 0.5 })));
  // parks
  for (let i = 0; i < 4; i++) {
    const a = r() * Math.PI * 2; const d = 1.6 + r() * 4;
    g.append(s('ellipse', { class: 'city-park', cx: cx + Math.cos(a) * d, cy: cy + Math.sin(a) * d, rx: 0.35 + r() * 0.5, ry: 0.25 + r() * 0.35, transform: `rotate(${(r() * 90) | 0} ${cx} ${cy})` }));
  }
  // core grid (minor streets)
  const minor = [];
  const half = 3.2; const step = 0.42;
  const rot = (x, y) => [cx + x * Math.cos(theta) - y * Math.sin(theta), cy + x * Math.sin(theta) + y * Math.cos(theta)];
  for (let k = -half; k <= half; k += step) {
    const jitter = (r() - 0.5) * 0.12;
    minor.push(`M${pts([rot(-half, k + jitter)])}L${pts([rot(half, k + jitter)])}`);
    minor.push(`M${pts([rot(k + jitter, -half)])}L${pts([rot(k + jitter, half)])}`);
  }
  g.append(s('path', { class: 'st-minor', d: minor.join('') }));
  // ring roads
  for (const R of [2.1, 4.6]) {
    const ring = [];
    for (let i = 0; i <= 36; i++) { const a = (i / 36) * Math.PI * 2; const rr = R * (0.88 + 0.24 * Math.sin(a * 3 + R)); ring.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr * 0.86]); }
    g.append(s('polyline', { class: 'st-major-casing', points: pts(ring) }), s('polyline', { class: 'st-major', points: pts(ring) }));
  }
  // arterials radiating out of the centre
  const arterial = [];
  for (let i = 0; i < 7; i++) {
    const a = theta + (i / 7) * Math.PI * 2 + (r() - 0.5) * 0.3;
    const line = [];
    for (let t = 0; t <= 8; t++) { const d = t * 1.6; const wob = Math.sin(t * 0.9 + i) * 0.25; line.push([cx + Math.cos(a) * d - Math.sin(a) * wob, cy + Math.sin(a) * d + Math.cos(a) * wob]); }
    arterial.push(`M${pts(line).replace(/ /g, 'L')}`);
  }
  g.append(s('path', { class: 'st-major-casing', d: arterial.join('') }), s('path', { class: 'st-major', d: arterial.join('') }));
  // expressway through the city and past the RouteZero site
  const a = theta + Math.PI / 2;
  const hw = [[cx - Math.cos(a) * 14, cy - Math.sin(a) * 14], [cx, cy], [sx + (sx - cx) * 0.2, sy + (sy - cy) * 0.2], [cx + Math.cos(a) * 14, cy + Math.sin(a) * 14]];
  g.append(s('polyline', { class: 'st-hwy-casing', points: pts(hw) }), s('polyline', { class: 'st-hwy', points: pts(hw) }));
  // a river meeting the coast near the centre
  const river = [];
  const ra = theta + 2.2;
  for (let t = 0; t <= 10; t++) { const d = t * 0.9; river.push([cx + Math.cos(ra) * d + Math.sin(t * 0.8) * 0.3, cy + Math.sin(ra) * d + Math.cos(t * 0.8) * 0.3]); }
  g.append(s('polyline', { class: 'city-river', points: pts(river) }));
  return g;
}

/** District label anchors (map units) for the overlay. */
export function districtAnchors(city) {
  const [cx, cy] = project(...city.at);
  return city.districts.map(([name, dx, dy]) => ({ name, x: cx + dx, y: cy + dy }));
}
